import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui/Logo";
import { Link } from "react-router-dom";
import { Footer } from "@/components/layout/Footer";
import { MetaTags } from "@/components/seo/MetaTags";

const Terms = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <MetaTags 
        title="Terms of Service - Ritual"
        description="Read the terms and conditions that govern your use of Ritual's services and platform."
      />
      
      <nav className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container flex h-14 sm:h-16 items-center justify-between px-4 sm:px-6">
          <Link to="/">
            <Logo size="sm" />
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm">Back</Button>
          </Link>
        </div>
      </nav>

      <main className="flex-1">
        <div className="container max-w-3xl py-8 sm:py-16 px-4 sm:px-6">
          <h1 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8">Terms of Service</h1>
          
          <div className="prose prose-gray dark:prose-invert max-w-none space-y-6 text-sm sm:text-base">
            <section>
              <p className="text-muted-foreground">Last updated: {new Date().toLocaleDateString()}</p>
              <p>
                These Terms of Service ("Terms") govern your access to and use of Ritual's website, applications, 
                and services (collectively, the "Services"). Please read these Terms carefully before using our Services.
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold mt-8 mb-4">1. Acceptance of Terms</h2>
              <p className="text-muted-foreground">
                By accessing or using our Services, you agree to be bound by these Terms and our Privacy Policy. 
                If you do not agree to these Terms, you may not access or use the Services. If you are using the 
                Services on behalf of an organization, you represent that you have authority to bind that organization 
                to these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold mt-8 mb-4">2. Description of Services</h2>
              <p className="text-muted-foreground">
                Ritual provides an operations management platform that helps teams create, execute, and track 
                standard operating procedures (SOPs). Our Services include task management, proof capture, 
                team coordination, and analytics features.
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold mt-8 mb-4">3. Account Registration</h2>
              <p className="text-muted-foreground mb-4">
                To use certain features of our Services, you must register for an account. When you register, you agree to:
              </p>
              <ul className="list-disc pl-5 text-muted-foreground space-y-2">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and promptly update your account information</li>
                <li>Maintain the security and confidentiality of your login credentials</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold mt-8 mb-4">4. Acceptable Use</h2>
              <p className="text-muted-foreground mb-4">You agree not to:</p>
              <ul className="list-disc pl-5 text-muted-foreground space-y-2">
                <li>Use the Services for any unlawful purpose</li>
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe the rights of others</li>
                <li>Interfere with or disrupt the Services</li>
                <li>Attempt to gain unauthorized access to any part of the Services</li>
                <li>Use the Services to transmit malware or harmful code</li>
                <li>Collect or harvest user information without consent</li>
                <li>Use automated systems to access the Services without permission</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold mt-8 mb-4">5. User Content</h2>
              <p className="text-muted-foreground">
                You retain ownership of content you create, upload, or share through the Services ("User Content"). 
                By submitting User Content, you grant us a worldwide, non-exclusive, royalty-free license to use, 
                store, display, and distribute that content solely for the purpose of providing the Services. You 
                are responsible for ensuring you have the rights to any content you submit.
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold mt-8 mb-4">6. Subscription and Payments</h2>
              <p className="text-muted-foreground">
                Some features of our Services require a paid subscription. By subscribing, you agree to pay all 
                applicable fees. Subscriptions automatically renew unless cancelled before the renewal date. 
                We may change our pricing with reasonable notice. Refunds are provided in accordance with our 
                Refund Policy.
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold mt-8 mb-4">7. Intellectual Property</h2>
              <p className="text-muted-foreground">
                The Services and their original content (excluding User Content), features, and functionality are 
                owned by Ritual and are protected by international copyright, trademark, and other intellectual 
                property laws. Our trademarks may not be used without our prior written permission.
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold mt-8 mb-4">8. Termination</h2>
              <p className="text-muted-foreground">
                We may terminate or suspend your account and access to the Services immediately, without prior 
                notice, for any breach of these Terms. You may terminate your account at any time by contacting 
                us. Upon termination, your right to use the Services will cease immediately.
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold mt-8 mb-4">9. Disclaimers</h2>
              <p className="text-muted-foreground">
                THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND. WE DISCLAIM 
                ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, 
                AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED OR ERROR-FREE.
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold mt-8 mb-4">10. Limitation of Liability</h2>
              <p className="text-muted-foreground">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, RITUAL SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
                SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICES. OUR TOTAL 
                LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE MONTHS PRECEDING THE CLAIM.
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold mt-8 mb-4">11. Indemnification</h2>
              <p className="text-muted-foreground">
                You agree to indemnify and hold harmless Ritual and its officers, directors, employees, and agents 
                from any claims, damages, losses, or expenses arising from your use of the Services or violation 
                of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold mt-8 mb-4">12. Governing Law</h2>
              <p className="text-muted-foreground">
                These Terms shall be governed by and construed in accordance with the laws of India, without 
                regard to conflict of law provisions. Any disputes shall be resolved in the courts of Mumbai, India.
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold mt-8 mb-4">13. Changes to Terms</h2>
              <p className="text-muted-foreground">
                We reserve the right to modify these Terms at any time. We will notify you of material changes 
                by posting the updated Terms and updating the "Last updated" date. Your continued use of the 
                Services after changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold mt-8 mb-4">14. Contact Us</h2>
              <p className="text-muted-foreground">
                If you have any questions about these Terms, please contact us at:{" "}
                <a href="mailto:legal@ritual.app" className="text-primary hover:underline">
                  legal@ritual.app
                </a>
              </p>
            </section>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Terms;
